import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/api";
import {
  HandsPraying,
  BookOpen,
  Compass,
  UsersThree,
  ArrowRight,
  ShieldCheck,
  CheckCircle,
  Sparkle,
  Church,
  Quotes,
  ChatCircleDots,
  HeartStraight,
  Wind,
} from "@phosphor-icons/react";

const trust = [
  {
    icon: ShieldCheck,
    title: "Scripture-Grounded",
    desc: "Every response is anchored in Scripture—with references you can verify and return to.",
  },
  {
    icon: CheckCircle,
    title: "Theological Integrity",
    desc: "Reviewed against trusted teaching to ensure guidance remains faithful—not opinion-driven.",
  },
  {
    icon: Sparkle,
    title: "Aligned with Biblical Principles",
    desc: "Designed to reflect the wisdom of Scripture in every interaction—not reinterpret it.",
  },
  {
    icon: Church,
    title: "An Extension of the Church",
    desc: "Not a replacement—something that walks alongside your faith, your church, and your life.",
  },
];

const scenarios = [
  {
    title: "When your world stops",
    detail: "Diagnosis. Loss. Confusion.",
    line: "You don't need content—you need words.",
    promise: "Voice helps you pray when you can't.",
    icon: HeartStraight,
  },
  {
    title: "When everything's noise",
    detail: "Anxiety. Pressure. A racing mind.",
    line: "You don't need another feed—you need a breath.",
    promise: "Voice gives you a Scripture-rooted way to slow down.",
    icon: Wind,
  },
  {
    title: "When your faith feels dry",
    detail: "Distance. Doubt. Going through motions.",
    line: "You don't need more theology—you need to pray honestly again.",
    promise: "Voice gives you words you can actually bring to God.",
    icon: HandsPraying,
  },
];

const howItWorks = [
  { n: "01", title: "Share What You're Facing", sub: "A situation, question, or prayer need." },
  { n: "02", title: "Receive Scripture-Guided Response", sub: "Not opinion—rooted in the Bible." },
  { n: "03", title: "Enter a Guided Prayer", sub: "Structured words when you don't have them." },
  { n: "04", title: "Invite or Join Others", sub: "Pray together, not alone." },
  { n: "05", title: "Continue the Journey", sub: "Build rhythm, not one-time use." },
];

const community = [
  {
    icon: HandsPraying,
    title: "Pray Together in Real Time",
    sub: "Not just requests—shared prayer moments.",
    bullets: [
      "Join live or ongoing prayer circles",
      "Others pray with you, not just for you",
      "Guided by Scripture, not silence",
    ],
  },
  {
    icon: UsersThree,
    title: "Small, Intentional Groups",
    sub: "Not a feed. Not noise. Real connection.",
    bullets: [
      "Groups based on life stage or need",
      "Focused, smaller circles—not mass chat",
      "Built for consistency and trust",
    ],
  },
  {
    icon: BookOpen,
    title: "Scripture-Centered Conversations",
    sub: "No drifting. No chaos.",
    bullets: [
      "Conversations anchored in Scripture",
      "Guided tone—no opinion spirals",
      "Designed for growth, not debate",
    ],
  },
];

const testimonials = [
  {
    quote:
      "I didn't know how to pray after the diagnosis. This gave me words—and people who stayed with me in it.",
    attribution: "After a hard week",
  },
  {
    quote:
      "When my faith felt dry, the guided prayers gave me something real to bring to God again.",
    attribution: "Returning to prayer",
  },
  {
    quote:
      "I came in wanting answers and walked out with Scripture I could actually use Monday morning.",
    attribution: "Caught between Sundays",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen overflow-hidden">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 h-14 flex items-center justify-between">
          <span className="font-heading tracking-[0.2em] text-base text-[#2D1B0E]">
            VO<span className="text-[#C4A265]">&#10013;</span>CE
          </span>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" className="text-[#8B7355] hover:text-[#5C4033] hover:bg-white/40 rounded-full font-body text-sm" data-testid="landing-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "nav" })}>
              <Button className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-5 font-body text-sm" data-testid="landing-cta-btn">
                Start with Voice
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center justify-center pt-16 pb-16" data-testid="hero-section">
        <div className="spiritual-orb w-[28rem] h-[28rem] top-1/4 left-1/2 -translate-x-1/2 animate-orb opacity-50" />
        <div className="relative z-10 px-6 max-w-5xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
          <div>
            <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-5 animate-fade-in-up">
              Scripture-grounded · For real life
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#2D1B0E] leading-[1.05] mb-5 animate-fade-in-up stagger-1 font-medium">
              Scripture-Guided Prayer
              <br />
              <span className="gradient-text-warm">for Real Life Moments.</span>
            </h1>
            <p className="text-[#5C4033] text-base sm:text-lg max-w-xl mb-8 font-body leading-relaxed animate-fade-in-up stagger-2">
              Scripture-guided prayer, real community, and spiritual direction—built for real life.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 animate-fade-in-up stagger-3 mb-6">
              <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "hero_primary" })}>
                <Button className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-8 py-6 text-base font-body" data-testid="hero-cta-btn">
                  Start Your First Guided Prayer <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/try" onClick={() => track("landing_cta_click", { placement: "hero_try" })}>
                <Button variant="outline" className="border-[#D4C9BA] hover:bg-white/50 text-[#5C4033] rounded-full px-8 py-6 text-base font-body w-full sm:w-auto" data-testid="hero-try-btn">
                  Try It Now—No Signup
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[#8B7355] text-xs font-body animate-fade-in-up stagger-3">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={14} weight="fill" className="text-[#C4A265]" />
                Scripture-Grounded
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#C4A265]" />
                Theological Integrity
              </span>
              <span className="flex items-center gap-1.5">
                <Church size={14} weight="fill" className="text-[#C4A265]" />
                An Extension of the Church
              </span>
            </div>
          </div>

          {/* Right: live product cue card */}
          <div className="relative animate-fade-in-up stagger-2" data-testid="hero-cue-card">
            <div className="glass-card-strong p-7 max-w-md mx-auto">
              <div className="flex items-center gap-2 mb-3 text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                127 believers praying right now
              </div>
              <Quotes size={20} weight="fill" className="text-[#C4A265] mb-2" />
              <p className="font-heading text-xl text-[#2D1B0E] leading-snug mb-3">
                "I didn't know how to pray for my mom's diagnosis. Voice gave me Scripture and words I could actually pray—with people who stayed with me in it."
              </p>
              <p className="text-[#8B7355] font-body text-xs">— After a hard week</p>
            </div>
          </div>
        </div>
      </section>

      {/* SCENARIOS — when your world stops */}
      <section className="py-24 px-6 sm:px-12 bg-white/30" data-testid="scenarios-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3 text-center">
            For real life
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] text-center mb-3 font-medium">
            When you don't have words.
          </h2>
          <p className="text-[#5C4033] text-center max-w-2xl mx-auto mb-12 font-body text-sm sm:text-base">
            Faith for the moments that don't fit on a Sunday.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {scenarios.map((s, i) => (
              <div key={i} className="glass-card-strong p-7 transition-all hover:-translate-y-1" data-testid={`scenario-${i}`}>
                <s.icon size={28} weight="duotone" className="text-[#C4A265] mb-4" />
                <h3 className="font-heading text-xl text-[#2D1B0E] font-medium mb-1.5">{s.title}</h3>
                <p className="text-[#8B7355] font-body text-xs uppercase tracking-wider mb-3">{s.detail}</p>
                <p className="text-[#5C4033] font-body text-sm mb-2 leading-relaxed">{s.line}</p>
                <p className="text-[#2D1B0E] font-heading text-base italic">{s.promise}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT INTERACTION — show, don't tell */}
      <section className="py-24 px-6 sm:px-12" data-testid="product-preview-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3 text-center">
            See it in action
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] text-center mb-3 font-medium">
            Not content. Conversation with structure.
          </h2>
          <p className="text-[#5C4033] text-center max-w-2xl mx-auto mb-12 font-body text-sm sm:text-base">
            You don't just read Scripture—you're guided through it.
          </p>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* Left: Sample Scripture-guided conversation */}
            <div className="glass-card-strong p-6 sm:p-7" data-testid="sample-conversation">
              <div className="flex items-center gap-2 mb-4">
                <ChatCircleDots size={18} weight="duotone" className="text-[#C4A265]" />
                <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium">
                  Scripture-Guided Conversation
                </p>
              </div>
              {/* User bubble */}
              <div className="mb-4">
                <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-1">You</p>
                <div className="bg-[#FAF5EC] border border-[#E8DFD3] rounded-2xl rounded-tl-sm px-4 py-3">
                  <p className="font-body text-sm text-[#5C4033] leading-relaxed">
                    My mother was diagnosed yesterday. I don't know how to pray for her.
                  </p>
                </div>
              </div>
              {/* Assistant bubble */}
              <div>
                <p className="text-[#C4A265] text-[10px] uppercase tracking-wider font-body mb-1">Voice</p>
                <div className="bg-white/70 border border-[#E8DFD3] rounded-2xl rounded-tr-sm px-4 py-3">
                  <p className="font-heading text-sm italic text-[#2D1B0E] leading-relaxed mb-2">
                    "Cast all your anxiety on him because he cares for you." — 1 Peter 5:7
                  </p>
                  <p className="font-body text-sm text-[#5C4033] leading-relaxed mb-2">
                    Lord, You see her name and her body. We don't carry this alone—You carry us. Steady our fear with Your nearness, and let Your peace guard her room and our hearts today.
                  </p>
                  <p className="font-body text-[11px] text-[#8B7355] mt-3 pt-3 border-t border-[#E8DFD3]">
                    Continue with a 6-step prayer journey for healing →
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Sample 1-2 step guided prayer */}
            <div className="glass-card-strong p-6 sm:p-7 relative overflow-hidden" data-testid="sample-journey">
              <div className="spiritual-orb-sm w-32 h-32 -bottom-10 -right-8 animate-orb opacity-40" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-4">
                  <HandsPraying size={18} weight="duotone" className="text-[#C4A265]" />
                  <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium">
                    Guided Prayer · A First Step Toward Peace
                  </p>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1.5 w-8 rounded-full bg-[#C4A265]" />
                  <div className="h-1.5 w-4 rounded-full bg-[#D4C9BA]" />
                  <span className="text-[#8B7355] text-[10px] font-body ml-1">Step 1 of 2 — 3 minutes</span>
                </div>
                <div className="bg-white/60 border border-[#E8DFD3] rounded-xl p-4 mb-3">
                  <Quotes size={14} weight="fill" className="text-[#C4A265] mb-1" />
                  <p className="font-heading text-sm italic text-[#2D1B0E] leading-relaxed mb-2">
                    "Be still, and know that I am God." — Psalm 46:10
                  </p>
                  <p className="font-body text-sm text-[#5C4033] leading-relaxed">
                    Take a slow breath in… and a slow breath out. Wherever you are right now—busy, anxious, hopeful, tired—God is already with you. You don't have to come to Him polished. You only have to come.
                  </p>
                </div>
                <Link to="/try" onClick={() => track("landing_cta_click", { placement: "preview_inline_try" })}>
                  <Button className="w-full bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full py-5 font-body text-sm" data-testid="preview-try-btn">
                    Walk this prayer — no signup <ArrowRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST — tightened */}
      <section className="py-20 px-6 sm:px-12 bg-white/30" data-testid="trust-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3 text-center">
            Why You Can Trust Voice
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] text-center mb-3 font-medium">
            Faithful. Verifiable. Not opinion-driven.
          </h2>
          <p className="text-[#5C4033] text-center max-w-2xl mx-auto mb-12 font-body text-sm sm:text-base">
            AI used responsibly, with theological integrity—built to support your faith, never replace your church.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {trust.map((t, i) => (
              <div key={i} className="glass-card-strong p-6" data-testid={`trust-${i}`}>
                <t.icon size={26} weight="duotone" className="text-[#C4A265] mb-3" />
                <h3 className="font-heading text-lg text-[#2D1B0E] mb-1.5 font-medium">{t.title}</h3>
                <p className="text-[#8B7355] font-body text-xs leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 5 steps */}
      <section className="py-24 px-6 sm:px-12" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3 text-center">
            How Voice Works
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] text-center mb-3 font-medium">
            Five steps. Built for real life.
          </h2>
          <p className="text-[#5C4033] text-center max-w-2xl mx-auto mb-12 font-body text-sm sm:text-base">
            From the moment you arrive to the rhythm you build—structure, not browsing.
          </p>
          <ol className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {howItWorks.map((s) => (
              <li key={s.n} className="glass-card p-5 relative" data-testid={`how-step-${s.n}`}>
                <span className="absolute -top-2 left-5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#2D1B0E] text-white font-heading text-xs">
                  {s.n}
                </span>
                <h3 className="font-heading text-base text-[#2D1B0E] font-medium mt-3 mb-1.5 leading-snug">
                  {s.title}
                </h3>
                <p className="text-[#8B7355] font-body text-xs leading-relaxed">{s.sub}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CORE VALUE — Faith for Real Moments */}
      <section className="py-20 px-6 sm:px-12 bg-white/30" data-testid="core-value-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3">
            Faith for the in-between
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#2D1B0E] mb-4 font-medium leading-tight">
            Faith for Real Moments—<span className="gradient-text-warm">Not Just Sunday.</span>
          </h2>
          <p className="text-[#5C4033] font-body text-base sm:text-lg leading-relaxed">
            When life gets heavy, unclear, or overwhelming—get Scripture you can actually use, and prayer you can actually say.
          </p>
        </div>
      </section>

      {/* THREE PRODUCT PILLARS — guided / scripture / rhythm */}
      <section className="py-24 px-6 sm:px-12" data-testid="pillars-section">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card-strong p-7 transition-all hover:-translate-y-1" data-testid="pillar-prayer">
            <HandsPraying size={32} weight="duotone" className="text-[#C4A265] mb-4" />
            <h3 className="font-heading text-xl text-[#2D1B0E] mb-2 font-medium leading-snug">
              Know What to Pray—Even When You Don't Have the Words
            </h3>
            <p className="text-[#5C4033] font-body text-sm mb-4 leading-relaxed">
              Step into guided prayer journeys for healing, peace, direction, and family—anchored in Scripture, not guesswork.
            </p>
            <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "pillar_prayer" })}
              className="inline-flex items-center gap-1 text-[#C4A265] hover:text-[#5C4033] font-body text-sm font-medium">
              Begin a Guided Prayer <ArrowRight size={14} />
            </Link>
          </div>
          <div className="glass-card-strong p-7 transition-all hover:-translate-y-1" data-testid="pillar-guidance">
            <Compass size={32} weight="duotone" className="text-[#C4A265] mb-4" />
            <h3 className="font-heading text-xl text-[#2D1B0E] mb-2 font-medium leading-snug">
              Get Scripture-Based Direction When You Need It Most
            </h3>
            <p className="text-[#5C4033] font-body text-sm mb-4 leading-relaxed">
              Ask hard questions and receive answers rooted in Scripture—so you can go back to your Bible with clarity.
            </p>
            <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "pillar_guidance" })}
              className="inline-flex items-center gap-1 text-[#C4A265] hover:text-[#5C4033] font-body text-sm font-medium">
              Ask for Guidance <ArrowRight size={14} />
            </Link>
          </div>
          <div className="glass-card-strong p-7 transition-all hover:-translate-y-1" data-testid="pillar-rhythm">
            <BookOpen size={32} weight="duotone" className="text-[#C4A265] mb-4" />
            <h3 className="font-heading text-xl text-[#2D1B0E] mb-2 font-medium leading-snug">
              Build a Daily Spiritual Rhythm
            </h3>
            <p className="text-[#5C4033] font-body text-sm mb-4 leading-relaxed">
              Structured paths for prayer, Scripture, and growth—built to fit real schedules, not ideal ones.
            </p>
            <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "pillar_rhythm" })}
              className="inline-flex items-center gap-1 text-[#C4A265] hover:text-[#5C4033] font-body text-sm font-medium">
              Explore Learning Paths <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* COMMUNITY PILLAR — walk your faith with others */}
      <section className="py-24 px-6 sm:px-12 bg-white/30" data-testid="community-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3 inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              127 believers praying right now
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] mb-3 font-medium">
              Walk Your Faith With Others—Not Alone.
            </h2>
            <p className="font-heading text-base sm:text-lg text-[#8B7355] italic">
              Real people. Real prayer. Rooted in Scripture—together.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {community.map((c, i) => (
              <div key={i} className="glass-card-strong p-7 flex flex-col" data-testid={`community-card-${i}`}>
                <c.icon size={30} weight="duotone" className="text-[#C4A265] mb-4" />
                <h3 className="font-heading text-lg text-[#2D1B0E] font-medium mb-1.5 leading-snug">
                  {c.title}
                </h3>
                <p className="text-[#8B7355] font-body text-xs uppercase tracking-wider mb-3">
                  {c.sub}
                </p>
                <ul className="text-[#5C4033] font-body text-sm space-y-1.5 mb-4">
                  {c.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C4A265] mt-2 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "community_pillar" })}>
              <Button className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-8 py-5 text-base font-body" data-testid="community-cta-btn">
                Start Praying With Others <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — situation-led */}
      <section className="py-20 px-6 sm:px-12" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3 text-center">
            From believers walking it out
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] text-center mb-10 font-medium">
            Real moments. Real prayer.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <div key={i} className="glass-card p-6 flex flex-col" data-testid={`testimonial-${i}`}>
                <Quotes size={18} weight="fill" className="text-[#C4A265] mb-2" />
                <p className="font-body text-sm text-[#5C4033] leading-relaxed mb-4 flex-1 italic">
                  "{t.quote}"
                </p>
                <p className="font-body text-[10px] text-[#8B7355] uppercase tracking-wider">
                  — {t.attribution}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-6" data-testid="cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <div className="glass-card-strong p-12 relative overflow-hidden">
            <div className="spiritual-orb-sm w-40 h-40 -top-10 -right-10 animate-orb" />
            <div className="relative z-10">
              <p className="text-[#C4A265] text-xs uppercase tracking-[0.3em] font-body mb-3">
                Take the first step
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl text-[#2D1B0E] mb-3 font-medium">
                Begin a Guided Prayer Journey
              </h2>
              <p className="text-[#5C4033] font-body text-sm sm:text-base mb-8 max-w-md mx-auto">
                Simple steps to find peace, clarity, and direction—rooted in Scripture and walked together.
              </p>
              <Link to="/auth" onClick={() => track("landing_cta_click", { placement: "footer_cta" })}>
                <Button className="bg-[#2D1B0E] hover:bg-[#3D2B1F] text-white rounded-full px-8 py-5 text-base font-body" data-testid="cta-get-started-btn">
                  Start With Voice <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <p className="text-[#8B7355] text-xs font-body mt-5">
                Free to begin. No credit card required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-[#E8DFD3]/50">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-heading tracking-[0.2em] text-sm text-[#2D1B0E]">
            VO<span className="text-[#C4A265]">&#10013;</span>CE
          </span>
          <p className="text-[#8B7355] text-xs font-body text-center sm:text-right">
            AI-assisted spiritual guidance, grounded in Scripture. Not a replacement for your local church or pastoral counsel.
          </p>
        </div>
      </footer>
    </div>
  );
}
